/**
 * Library: the one home for every clip, end to end. This is where clips load into the Analyser and
 * Tagging from, where analysing progress shows in-row, and where bulk work happens (auto-analyse,
 * re-analyse, hide, share, delete). Searchable across names, tags, spoken words and snippet
 * descriptions; sortable by the column headers; filterable by segmentation state (AI-segmented,
 * user-tuned, awaiting); a page at a time, the pager ending the filter row. Display names are
 * editable; the original filename stays for dedupe.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { TYPEAHEAD_DEBOUNCE_MS, analyseDurationWords, transcribeSeconds } from '@studio/contracts';
import { useAddFiles } from '@/components/add-files';
import { HeldClip } from '@/components/held-clip';
import { AnimateConfirm, animationLabel, type AnimateTarget } from '@/components/animate-still';
import { AudioTable, type AudioRouteFilter } from '@/components/audio-table';
import { useComposerActions } from '@/components/composer-actions';
import { Icon } from '@/components/icons';
import { RowMenu } from '@/components/row-menu';
import { StarRating, ratingMenuItem } from '@/components/star-rating';
import { TopBar } from '@/components/shell';
import { TagAssetsModal } from '@/components/tag-assets-modal';
import { Pager, pageCount } from '@/components/pager';
import { PartsModal } from '@/components/parts-modal';
import { RenameClipModal } from '@/components/rename-clip-modal';
import { useRatings } from '@/hooks/use-ratings';
import { fmtBytes, fmtTime, fmtWhen } from '@/lib/format';
import { queryClient, trpc } from '@/lib/trpc';
import { AnalyseProgress } from '@/routes/analyse';

type Tab = 'active' | 'hidden' | 'all' | 'deleted' | 'shared';
const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'hidden', label: 'Hidden' },
  { key: 'deleted', label: 'Deleted' },
  { key: 'shared', label: 'Shared with you' },
];
type Seg = 'all' | 'awaiting' | 'segmented' | 'ai' | 'user';
type Sort = 'created' | 'name' | 'size' | 'length' | 'snippets';
const RIGHTS_WARNING_DAYS = 60;
const LIB_POLL_MS = 4000;

export function LibraryPage() {
  const { me } = useRouteContext({ from: '/_app' });
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/library' });
  const composer = useComposerActions();
  // Video (the clips), Photos (the stills, 2026-10-05) or Audio (the sound library as a table, 2026-10-04).
  const [media, setMedia] = useState<'video' | 'photos' | 'audio'>(search.media === 'audio' || search.pack ? 'audio' : search.media === 'photos' ? 'photos' : 'video');
  const photos = media === 'photos';
  const kind = photos ? 'image' as const : 'video' as const;
  const [animating, setAnimating] = useState<AnimateTarget | null>(null);
  const addFiles = useAddFiles();
  const [audioRoute, setAudioRoute] = useState<AudioRouteFilter>(null);
  const [pack, setPack] = useState<string | null>(search.pack ?? null);
  const packs = useQuery(trpc.search.packs.queryOptions({ include: pack }, { enabled: media === 'audio' }));
  const [tab, setTab] = useState<Tab>('all');
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [rights, setRights] = useState<'any' | 'ending' | 'ended'>('any');
  const [seg, setSeg] = useState<Seg>('all');
  const [sort, setSort] = useState<Sort>('created');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [shareTo, setShareTo] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [tagging, setTagging] = useState<string | null>(null);
  const [partsOf, setPartsOf] = useState<string | null>(null);
  const combine = useMutation(trpc.assets.combine.mutationOptions({ onSuccess: () => { setPicked(new Set()); void queryClient.invalidateQueries({ queryKey: trpc.library.list.queryKey() }); } }));

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), TYPEAHEAD_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const stats = useQuery(trpc.library.stats.queryOptions({ kind }));
  const creators = useQuery(trpc.library.creators.queryOptions());
  const [page, setPage] = useState(1);
  // A new tab, search, filter or order starts again from the first page.
  const scope = JSON.stringify([kind, tab, creatorId, rights, debounced, seg, sort, dir]);
  useEffect(() => setPage(1), [scope]);
  const listInput = { kind, tab, creatorId, rights, q: debounced || null, seg: photos ? 'all' as const : seg, sort, dir, page };
  const list = useQuery(trpc.library.list.queryOptions(listInput, {
    placeholderData: (p) => p,
    refetchInterval: (query) => (query.state.data?.rows.some((a) => a.status === 'awaiting_upload' || a.progressStage !== null || a.animation?.status === 'queued' || a.animation?.status === 'running') ? LIB_POLL_MS : false),
  }));
  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const pageSize = list.data?.pageSize ?? 1;
  const pages = pageCount(total, pageSize);
  // A deleted clip is a stub with nothing left to rate.
  const ratingTargets = useMemo(() => rows.filter((a) => a.status !== 'deleted').map((a) => ({ kind: 'asset' as const, id: a.id })), [rows]);
  const ratings = useRatings(ratingTargets);
  // A page left past the end (clips hidden or deleted meanwhile) steps back to the last one.
  useEffect(() => {
    if (list.data && page > pages) setPage(pages);
  }, [list.data, page, pages]);

  const refresh = async () => {
    setPicked(new Set());
    setConfirmDelete(false);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.library.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.library.stats.queryKey() }),
    ]);
  };
  const hide = useMutation(trpc.library.hide.mutationOptions({ onSuccess: refresh }));
  // Long clips analysed without their speech (Dax, 2026-10-06): transcribe one, or switch the brand to do it always.
  const transcribe = useMutation(trpc.assets.transcribe.mutationOptions({ onSuccess: refresh }));
  const speechDefaults = useQuery(trpc.brandSettings.analyseDefaults.queryOptions());
  const setTranscribeLong = useMutation(trpc.brandSettings.setTranscribeLong.mutationOptions({
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.brandSettings.analyseDefaults.queryKey() }),
  }));
  const restore = useMutation(trpc.library.restore.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(trpc.library.remove.mutationOptions({ onSuccess: refresh }));
  const share = useMutation(trpc.grants.share.mutationOptions({ onSuccess: refresh }));
  const reanalyseMany = useMutation(trpc.assets.reanalyseMany.mutationOptions({ onSuccess: refresh }));
  const duplicateComp = useMutation(trpc.compose.duplicate.mutationOptions({
    onSuccess: (data) => void navigate({ to: '/compose', search: { draft: data.draftId } }),
  }));

  const toggle = (id: string) => {
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const ids = [...picked];
  const isOwner = me.active?.role === 'owner';
  const shareTargets = me.brands.filter((b) => b.brandId !== me.active?.brandId);
  // A failed analysis (awaiting_upload with an error) stays selectable so it can be re-queued.
  const selectable = rows.filter((a) => (a.status !== 'awaiting_upload' || a.error) && a.status !== 'shared');
  const openInAnalyser = (id: string) => void navigate({ to: '/analyse', search: { asset: id } });
  // A row click returns to the dashboard that sent the user here; Tagging takes only indexed clips. A still
  // has no timeline to analyse, so it always opens in Tagging.
  const openRow = (a: { id: string; status: string }) => {
    if ((photos || search.from === 'tagging') && a.status === 'indexed') void navigate({ to: '/tagging', search: { asset: a.id } });
    else if (!photos) openInAnalyser(a.id);
  };

  const doShare = () => {
    const target = shareTargets.find((b) => b.brandId === shareTo);
    if (!target || !ids.length) return;
    share.mutate({ targetKind: 'asset', targetIds: ids, granteeKind: 'brand', granteeId: target.brandId });
    setShareTo('');
  };

  /** A clickable header that cycles its sort direction; the arrow marks the active column. */
  const header = (label: string, key: Sort, numeric = false) => (
    <th className={numeric ? 'n' : ''}>
      <button type="button" className={`th-sort${sort === key ? ' on' : ''}`}
        onClick={() => {
          if (sort === key) setDir(dir === 'desc' ? 'asc' : 'desc');
          else {
            setSort(key);
            setDir(key === 'name' ? 'asc' : 'desc');
          }
        }}>
        {label}{sort === key ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
      </button>
    </th>
  );

  return (
    <>
      <TopBar title="Library">
        <div className="seg" role="radiogroup" aria-label="Video, photos or audio">
          {(['video', 'photos', 'audio'] as const).map((m) => (
            <button key={m} type="button" role="radio" aria-checked={media === m} className={media === m ? 'on' : ''} onClick={() => { setMedia(m); setPicked(new Set()); }}>{m === 'video' ? 'Video' : m === 'photos' ? 'Photos' : 'Audio'}</button>
          ))}
        </div>
        {media !== 'audio' && stats.data && <span className="sub crumb">{stats.data.active} active · {stats.data.inFlight} in progress · {stats.data.hidden} hidden · {stats.data.deleted} deleted</span>}
        {media !== 'audio' && <div className="seg upload-tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={t.key === tab ? 'on' : ''} onClick={() => { setTab(t.key); setPicked(new Set()); }}>{t.label}</button>
          ))}
        </div>}
        <div className="right">
          {media !== 'audio' && ids.length > 0 && tab !== 'shared' && (
            <>
              <button type="button" className="btn" disabled={reanalyseMany.isPending}
                title="Repeat each clip's own last analysis settings"
                onClick={() => reanalyseMany.mutate({ assetIds: ids, mode: 'stored' })}>Re-analyse</button>
              {tab === 'hidden' ? <button type="button" className="btn" disabled={restore.isPending} onClick={() => restore.mutate({ assetIds: ids })}>Restore</button>
                : tab === 'deleted' ? null
                  : <button type="button" className="btn" disabled={hide.isPending} onClick={() => hide.mutate({ assetIds: ids })}>Hide from search</button>}
              {isOwner && !photos && ids.length >= 2 && tab !== 'deleted' && tab !== 'hidden' && (
                <button type="button" className="btn" disabled={combine.isPending} title="The selected clips become one project: one row, one timeline, their snippets and tags carried over, no second analysis. The separate clips are removed."
                  onClick={() => combine.mutate({ assetIds: ids })}>{combine.isPending ? 'Combining…' : `Combine ${ids.length} into one project`}</button>
              )}
              {isOwner && !confirmDelete && <button type="button" className="btn lib-danger" onClick={() => setConfirmDelete(true)}>Delete</button>}
              {isOwner && confirmDelete && (
                <>
                  <button type="button" className="btn lib-danger" disabled={remove.isPending} onClick={() => remove.mutate({ assetIds: ids })}>Confirm deletion of {ids.length}</button>
                  <button type="button" className="btn quiet" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </>
              )}
              {!photos && (
                <button type="button" className="btn primary" disabled={reanalyseMany.isPending}
                  title="Analyse the selected clips with the brand's automatic settings"
                  onClick={() => reanalyseMany.mutate({ assetIds: ids, mode: 'auto' })}>
                  {reanalyseMany.isPending ? 'Queuing…' : `Auto-analyse ${ids.length}`}
                </button>
              )}
            </>
          )}
          <button type="button" className="btn primary lg" onClick={() => void navigate({ to: '/upload' })}>+ Add to library</button>
        </div>
      </TopBar>
      <section className="content lib-content">
        {media === 'audio' ? (
          <>
            <div className="row lib-filters">
              <label className="field lib-search">
                <svg className="filter-glass" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="m10.5 10.5 3 3" /></svg>
                <input value={q} placeholder="Search names, tags, descriptions, publishers…" aria-label="Search the sound library" onChange={(e) => setQ(e.target.value)} />
              </label>
              <div className="seg" role="radiogroup" aria-label="Music, sound effects or voiceovers">
                {([null, 'sfx', 'music', 'voice'] as const).map((r) => (
                  <button key={r ?? 'all'} type="button" role="radio" aria-checked={audioRoute === r} className={audioRoute === r ? 'on' : ''}
                    onClick={() => setAudioRoute(r)}>{r === null ? 'All' : r === 'music' ? 'Music' : r === 'sfx' ? 'Sound effects' : 'Voiceover'}</button>
                ))}
              </div>
              {(packs.data?.length ?? 0) > 0 && (
                <label className="field browse-select">
                  <select value={pack ?? ''} aria-label="Import pack" onChange={(e) => setPack(e.target.value || null)}>
                    <option value="">Pack · Any</option>
                    {packs.data?.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.files})</option>)}
                  </select>
                </label>
              )}
            </div>
            <AudioTable route={audioRoute} q={debounced || null} pack={pack} onOpenPack={setPack} />
          </>
        ) : (<>
        <div className="row lib-filters">
          <label className="field lib-search">
            <svg className="filter-glass" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="m10.5 10.5 3 3" /></svg>
            <input value={q} placeholder="Search names, tags, spoken words, descriptions…" aria-label="Search the library" onChange={(e) => setQ(e.target.value)} />
          </label>
          {!photos && <label className="field browse-select">
            <select value={seg} aria-label="Segmentation" onChange={(e) => setSeg(e.target.value as Seg)}>
              <option value="all">Segmentation · Any</option>
              <option value="awaiting">Awaiting segmentation</option>
              <option value="segmented">Segmented</option>
              <option value="ai">AI-segmented</option>
              <option value="user">User-tuned</option>
            </select>
          </label>}
          <label className="field browse-select">
            <select value={creatorId ?? ''} aria-label="Filter by creator" onChange={(e) => setCreatorId(e.target.value || null)}>
              <option value="">Creator · Any</option>
              {creators.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="field browse-select">
            <select value={rights} aria-label="Filter by rights" onChange={(e) => setRights(e.target.value as typeof rights)}>
              <option value="any">Rights · Any</option>
              <option value="ending">Ending within {RIGHTS_WARNING_DAYS} days</option>
              <option value="ended">Ended</option>
            </select>
          </label>
          {isOwner && ids.length > 0 && shareTargets.length > 0 && (
            <div className="row lib-share">
              <label className="field lib-select">
                <select value={shareTo} aria-label="Share with" onChange={(e) => setShareTo(e.target.value)}>
                  <option value="">Share {ids.length} with…</option>
                  {shareTargets.map((b) => <option key={b.brandId} value={b.brandId}>{b.brandName} ({b.orgName})</option>)}
                </select>
              </label>
              <button type="button" className="btn" disabled={!shareTo || share.isPending} onClick={doShare}>Share</button>
            </div>
          )}
          {total > 0 && <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />}
        </div>
        <div className="row lib-warnings">
          {stats.data && stats.data.rightsEnding > 0 && <span className="chip warn">{stats.data.rightsEnding} asset{stats.data.rightsEnding === 1 ? ' has' : 's have'} rights ending within {RIGHTS_WARNING_DAYS} days</span>}
          <span className="sub lib-note">{photos ? 'Click a row to open it in Tagging' : 'Click a row to open it in the Analyser · AI marks an auto-segmented clip, Tuned a human-adjusted one'}</span>
        </div>
        <div className="card lib-table imports-table">
          <table className="tbl">
            <thead>
              <tr>
                <th className="check-cell">
                  {tab !== 'shared' && (
                    <input type="checkbox" aria-label="Select all"
                      checked={selectable.length > 0 && selectable.every((a) => picked.has(a.id))}
                      onChange={(e) => setPicked(e.target.checked ? new Set(selectable.map((a) => a.id)) : new Set())} />
                  )}
                </th>
                <th className="thumb-cell" />
                {header('Asset', 'name')}
                <th>Rating</th>
                <th>Creator</th>
                <th>Status</th>
                {!photos && <th>Last used</th>}
                {!photos && <th>Composition</th>}
                {header('Size', 'size', true)}
                {!photos && header('Length', 'length', true)}
                {!photos && header('Snippets', 'snippets', true)}
                {!photos && <th>Segmented</th>}
                {!photos && <th className="n">Used</th>}
                {header('Uploaded', 'created')}
                <th className="menu-cell" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className={`imports-row${picked.has(a.id) ? ' lib-picked' : ''}`}
                  onClick={() => { if (a.status !== 'deleted') openRow(a); }}>
                  <td className="check-cell" onClick={(e) => e.stopPropagation()}>
                    {tab !== 'shared' && a.status !== 'shared' && (
                      <input type="checkbox" aria-label={`Select ${a.name}`} disabled={a.status === 'awaiting_upload' && !a.error}
                        checked={picked.has(a.id)} onChange={() => toggle(a.id)} />
                    )}
                  </td>
                  <td className="thumb-cell">{a.posterUrl ? <img className="lib-thumb" src={a.posterUrl} alt="" /> : <span className="lib-thumb empty-poster" />}</td>
                  <td>
                    <span className="row lib-name-row">
                      <b className="lib-name">{a.name}</b>
                      {a.partCount > 1 && (
                        <button type="button" className="chip chip-btn lib-parts" title={`A project of ${a.partCount} files, one clip: see the files`}
                          onClick={(e) => { e.stopPropagation(); setPartsOf(a.id); }}><Icon name="folder" /> {a.partCount} files</button>
                      )}
                      {a.sourceName && <span className="chip lib-parts" title={`Animated from the photo "${a.sourceName}"`}><Icon name="wand" /> Animated</span>}
                      {photos && a.animated > 0 && <span className="chip lib-parts" title="Clips animated from this photo"><Icon name="wand" /> {a.animated} animated</span>}
                      {a.status !== 'shared' && a.status !== 'deleted' && (
                        <button type="button" className="name-edit" title="Rename" aria-label={`Rename ${a.name}`}
                          onClick={(e) => { e.stopPropagation(); setRenaming({ id: a.id, name: a.name }); }}>
                          <Icon name="pencil" />
                        </button>
                      )}
                    </span>
                    {a.facetSummary && <span className="sub lib-facets">{a.facetSummary}</span>}
                  </td>
                  <td>{a.status !== 'deleted' && <StarRating rating={ratings.ratingOf({ kind: 'asset', id: a.id })} onRate={(n) => ratings.rate({ kind: 'asset', id: a.id }, n)} />}</td>
                  <td className="sub">{a.creator ?? ''}</td>
                  <td>
                    {a.heldReason ? <HeldClip reason={a.heldReason} assetId={a.id} durationMs={a.durationMs} estimateMs={a.heldEstimateMs} />
                      : a.error ? <span className="chip bad" title={a.error}>analysis failed</span>
                      : animationLabel(a.animation) ? <span className={`chip ${animationLabel(a.animation)!.tone}`} title={animationLabel(a.animation)!.title}>{animationLabel(a.animation)!.text}</span>
                      : a.status === 'awaiting_upload' ? (
                        a.bytes === null ? <span className="chip">Uploading…</span> : (
                          <AnalyseProgress stage={a.progressStage} progress={a.analyseProgress} etaAt={a.analyseEtaAt} />
                        )
                      ) : a.progressStage ? <AnalyseProgress stage={a.progressStage} progress={a.analyseProgress} etaAt={a.analyseEtaAt} />
                      : <StatusChip status={a.status} rightsUntil={a.rightsUntil} used={a.used} />}
                  </td>
                  {!photos && <td className="sub nowrap" onClick={(e) => e.stopPropagation()}>
                    {a.lastUsedAt ? (
                      a.lastCompositionId ? (
                        <button type="button" className="imports-open" title={`Open "${a.lastComposition ?? 'composition'}" in the Composer`}
                          onClick={() => void navigate({ to: '/compose', search: { draft: a.lastCompositionId! } })}>{fmtWhen(a.lastUsedAt)}</button>
                      ) : fmtWhen(a.lastUsedAt)
                    ) : ''}
                  </td>}
                  {!photos && <td className="sub lib-comp" title={a.lastComposition ?? ''}>{a.lastComposition ? (a.lastComposition.length > 24 ? `${a.lastComposition.slice(0, 24)}…` : a.lastComposition) : ''}</td>}
                  <td className="n nowrap">{fmtBytes(a.bytes)}</td>
                  {!photos && <td className="n mono nowrap">{a.durationMs ? `${Math.round(a.durationMs / 1000)} s` : ''}</td>}
                  {!photos && <td className="n mono">{a.snippets || ''}</td>}
                  {!photos && <td>
                    <span className="row lib-flags">
                      {a.aiSegmented && <span className="chip acc" title="Segmented automatically by the AI">AI</span>}
                      {a.userTuned && <span className="chip ok" title="A human adjusted the segmentation or names">Tuned</span>}
                      {!a.aiSegmented && !a.userTuned && a.snippets > 0 && <span className="chip" title="Script-split, untouched">Split</span>}
                    </span>
                  </td>}
                  {!photos && <td className="n">{a.used || ''}</td>}
                  <td className="sub nowrap">{a.createdAt ? fmtWhen(a.createdAt) : ''}</td>
                  <td className="menu-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="row res-actions">
                      <RowMenu items={photos ? [
                        ...(a.status !== 'deleted' && a.status !== 'shared' ? [
                          { label: 'Animate this image', onClick: () => setAnimating({ id: a.id, name: a.name }), disabled: a.status !== 'indexed' },
                          { label: 'Tag this photo…', onClick: () => setTagging(a.id) },
                          { label: 'Open in Tagging', onClick: () => void navigate({ to: '/tagging', search: { asset: a.id } }), disabled: a.status !== 'indexed' },
                          { label: 'Rename…', onClick: () => setRenaming({ id: a.id, name: a.name }) },
                        ] : []),
                        ...(a.status !== 'deleted' ? [ratingMenuItem(ratings.ratingOf({ kind: 'asset', id: a.id }), (n) => ratings.rate({ kind: 'asset', id: a.id }, n))] : []),
                        ...(a.status === 'indexed' ? [{ label: 'Hide from search', onClick: () => hide.mutate({ assetIds: [a.id] }) }] : []),
                        ...(a.status === 'hidden' ? [{ label: 'Restore', onClick: () => restore.mutate({ assetIds: [a.id] }) }] : []),
                        ...(isOwner && a.status !== 'deleted' && a.status !== 'shared' ? [null, { label: 'Delete permanently', danger: true, onClick: () => remove.mutate({ assetIds: [a.id] }) }] : []),
                      ] : [
                        ...(a.status !== 'deleted' && a.status !== 'shared' ? [
                          { label: 'Open in the Analyser', onClick: () => openInAnalyser(a.id) },
                          { label: 'Tag this clip…', onClick: () => setTagging(a.id) },
                          // A long clip held for its go-ahead can be tagged already: its quick preview plays.
                          { label: 'Open in Tagging', onClick: () => void navigate({ to: '/tagging', search: { asset: a.id } }), disabled: a.status !== 'indexed' && a.heldReason !== 'long' },
                          { label: 'Rename…', onClick: () => setRenaming({ id: a.id, name: a.name }) },
                          ...(a.partCount > 1 ? [{ label: 'Parts…', onClick: () => setPartsOf(a.id) }] : []),
                          { label: a.partCount > 1 ? 'Add files to this project…' : 'Add files…', onClick: () => addFiles.pick({ assetId: a.id, name: a.name }), disabled: a.status === 'awaiting_upload' },
                        ] : []),
                        ...(a.untranscribed && a.status === 'indexed' ? [
                          { label: `Transcribe speech · ${analyseDurationWords(transcribeSeconds(a.durationMs ?? 0) * 1000)}`, onClick: () => transcribe.mutate({ assetId: a.id }), disabled: a.progressStage !== null },
                          ...(isOwner && speechDefaults.data && !speechDefaults.data.explicit.transcribeLong
                            ? [{ label: 'Transcribe long clips automatically', onClick: () => setTranscribeLong.mutate({ on: true }) }] : []),
                        ] : []),
                        ...(a.status !== 'deleted' ? [ratingMenuItem(ratings.ratingOf({ kind: 'asset', id: a.id }), (n) => ratings.rate({ kind: 'asset', id: a.id }, n))] : []),
                        ...(a.status === 'indexed' || a.status === 'shared' ? [{ label: 'Send all snippets to Composer', onClick: () => composer.sendAsset(a.id) }] : []),
                        ...(a.lastCompositionId ? [
                          { label: 'Open last composition', onClick: () => void navigate({ to: '/compose', search: { draft: a.lastCompositionId! } }) },
                          { label: 'Duplicate last composition', onClick: () => duplicateComp.mutate({ draftId: a.lastCompositionId!, name: null, asTemplate: false }) },
                        ] : []),
                        ...(a.status === 'indexed' ? [{ label: 'Hide from search', onClick: () => hide.mutate({ assetIds: [a.id] }) }] : []),
                        ...(a.status === 'hidden' ? [{ label: 'Restore', onClick: () => restore.mutate({ assetIds: [a.id] }) }] : []),
                        ...(isOwner && a.status !== 'deleted' && a.status !== 'shared' ? [null, { label: 'Delete permanently', danger: true, onClick: () => remove.mutate({ assetIds: [a.id] }) }] : []),
                      ]} />
                    </div>
                  </td>
                </tr>
              ))}
              {list.data && rows.length === 0 && <tr><td colSpan={photos ? 9 : 15} className="sub creator-empty">Nothing in this view.</td></tr>}
            </tbody>
          </table>
        </div>
        {/* The pager again at the table's foot (Dax, 2026-10-01), beside the footage on this page. */}
        {rows.length > 0 && (
          <div className="row pager-foot">
            {rows.some((a) => a.durationMs) && <span className="sub lib-count">{fmtTime(rows.reduce((s, a) => s + (a.durationMs ?? 0), 0), false)} of footage on this page</span>}
            <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
          </div>
        )}
        {(hide.isError || restore.isError || remove.isError || share.isError || reanalyseMany.isError || combine.isError) && (
          <div className="card pad error" role="alert">{hide.error?.message ?? restore.error?.message ?? remove.error?.message ?? share.error?.message ?? reanalyseMany.error?.message ?? combine.error?.message}</div>
        )}
        {tagging && <TagAssetsModal assetIds={[tagging]} onClose={() => setTagging(null)} />}
        {animating && <AnimateConfirm still={animating} onClose={() => setAnimating(null)} />}
        {addFiles.node}
        {partsOf && <PartsModal assetId={partsOf} canRemove={isOwner} onClose={() => setPartsOf(null)} />}
        {renaming && <RenameClipModal assetId={renaming.id} name={renaming.name} onClose={() => setRenaming(null)} />}
        </>)}
      </section>
    </>
  );
}

function StatusChip({ status, rightsUntil, used }: { status: string; rightsUntil: string | null; used: number }) {
  if (status === 'deleted') return <span className="chip bad">Deleted</span>;
  if (status === 'hidden') return <span className="chip warn">Hidden</span>;
  if (status === 'shared') return <span className="chip acc">Shared</span>;
  if (status === 'awaiting_snippet') return <span className="chip acc">Ready to segment</span>;
  if (status === 'confirming') return <span className="chip warn">Segmenting</span>;
  if (rightsUntil && new Date(rightsUntil).getTime() < Date.now() + RIGHTS_WARNING_DAYS * 86400000) return <span className="chip warn">Rights ending</span>;
  if (used === 0) return <span className="chip ok">In library</span>;
  return <span className="chip ok">Active</span>;
}